/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));

        if (this.dataset.showQuantitySelector) {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            this.lineItemChangeListener = this.lineItemChangeListener
              || this.handleLineItemChange.bind(this);
            document.addEventListener('on:line-item:change', (evt) => {
              this.lineItemChangeListener(evt);
            });
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        document.addEventListener('on:variant:change', this.setMaximumInputQuantity.bind(this));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
    }

    /**
     * Resolves the currently selected Shopify variant ID even when the theme's
     * hidden id input is disabled or absent from FormData.
     * @returns {?string}
     */
    resolveVariantId() {
      // Hidden inputs can be disabled (and therefore omitted from FormData),
      // but their DOM value is still the most authoritative value.
      const idInput = this.form?.querySelector('input[name="id"]');
      if (idInput?.value && /^\d+$/.test(String(idInput.value))) {
        return String(idInput.value);
      }

      if (this.dataset.variantId && /^\d+$/.test(String(this.dataset.variantId))) {
        return String(this.dataset.variantId);
      }

      const scope = this.closest('.product-info')
        || this.closest('.js-product')
        || this.closest('.shopify-section')
        || document;

      // The Canopy variant-picker custom element exposes the live selected
      // variant as `variant`, so use it before relying on markup fallbacks.
      const picker = scope.querySelector?.('variant-picker');
      const pickerVariantId = picker?.variant?.id;
      if (pickerVariantId && /^\d+$/.test(String(pickerVariantId))) {
        return String(pickerVariantId);
      }

      // Selected option controls often expose their resolved variant ID.
      const selectedOption = scope.querySelector?.(
        '.js-option:checked[data-variant-id], .js-option[aria-selected="true"][data-variant-id]'
      );
      if (selectedOption?.dataset?.variantId && /^\d+$/.test(String(selectedOption.dataset.variantId))) {
        return String(selectedOption.dataset.variantId);
      }

      // PDP URL is updated by variant-picker when the customer changes variant.
      try {
        const urlVariant = new URL(window.location.href).searchParams.get('variant');
        if (urlVariant && /^\d+$/.test(urlVariant)) return urlVariant;
      } catch (_) {}

      // Single-variant products may not render a variant picker at all. The
      // inventory JSON contains the actual variant IDs, so use the only variant
      // (or only available variant) as a final safe fallback.
      const inventoryEl = scope.querySelector?.('.js-inventory-data');
      if (inventoryEl) {
        try {
          const inventory = JSON.parse(inventoryEl.textContent || '[]');
          if (Array.isArray(inventory)) {
            if (inventory.length === 1 && inventory[0]?.id) {
              return String(inventory[0].id);
            }
            const available = inventory.filter((entry) => entry?.available && entry?.id);
            if (available.length === 1) return String(available[0].id);
          }
        } catch (_) {}
      }

      return null;
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const inventory = JSON.parse(dataEl.textContent);

            if (inventory) {
              if (inventory.inventory_management && inventory.inventory_quantity > 0 && inventory.inventory_policy !== 'continue') {
                quantityInput.max = inventory.inventory_quantity;

                const currentQuantity = Number(quantityInput.value);
                if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                  quantityInput.value = quantityInput.max;
                }
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    handleLineItemChange(evt) {
      if (evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    handleChange(evt) {
      this.updateQuantity(evt.target.value);
    }

    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();
      const customCartDrawer = window.FTRCart;
      const useCustomCartDrawer = customCartDrawer && typeof customCartDrawer.open === 'function';

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);

      // Shopify /cart/add requires a variant ID. Recover it explicitly because
      // disabled form controls are omitted from FormData and some PDP templates
      // do not expose data-variant-id on <product-form>.
      if (!formData.get('id')) {
        const resolvedVariantId = this.resolveVariantId();
        if (resolvedVariantId) {
          formData.set('id', resolvedVariantId);
          this.dataset.variantId = resolvedVariantId;
          console.log('[FTR Product Form] Recovered variant id:', resolvedVariantId);
        }
      }

      // Do not submit an empty selling_plan for normal one-time purchases.
      if (formData.has('selling_plan') && !String(formData.get('selling_plan') || '').trim()) {
        formData.delete('selling_plan');
      }

      if (!formData.get('id')) {
        const message = 'Unable to determine the selected product variant.';
        this.setErrorMsgState(message);
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');
        console.error('[FTR Product Form]', message, this.form);
        return;
      }

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer && !useCustomCartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        ProductForm.updateCartIcon(data);

        const quickAddDrawer = this.closest('quick-add-drawer');
        const isCartPage = window.location.pathname === theme.routes.cart;

        setTimeout(() => {
          const shouldOpenDrawer = !isCartPage;

          if (quickOrderSections.length) {
            quickOrderSections.forEach((quickOrderSection) => {
              const sectionEl = document.getElementById(quickOrderSection.id);
              if (!sectionEl) return;

              const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
              el.innerHTML = ProductForm.getElementHTML(
                data.sections[quickOrderSection.section],
                quickOrderSection.selector
              );
            });
          }

          if (quickAddDrawer && shouldOpenDrawer && typeof quickAddDrawer.close === 'function') {
            quickAddDrawer.close();
          }

          if (useCustomCartDrawer) {
            if (shouldOpenDrawer) {
              customCartDrawer.open();
            } else if (typeof customCartDrawer.refresh === 'function') {
              customCartDrawer.refresh();
            }
          } else if (this.cartDrawer) {
            this.cartDrawer.renderContents(
              data,
              shouldOpenDrawer
            );
          } else if (isCartPage) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refreshCartItems();
              }
            }
          }
        }, 700);

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        let newQuantity = 1;

        if (itemInOldCart) {
          newQuantity = (itemInOldCart.quantity === data.quantity)
            ? itemInOldCart.quantity : data.quantity;
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));

          if (this.quantitySelector) {
            this.setAttribute('data-show-quantity-selector', 'true');
            this.quantityInput.value = newQuantity;
            this.quantitySelector.currentQty = this.quantityInput.dataset.initialValue;
          }
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent || error.message
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    updateQuantityState(newQuantity) {
      this.quantityInput.value = newQuantity;
      this.quantitySelector.currentQty = newQuantity;
      this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
    }

    async updateQuantity(quantity) {
      this.quantitySelector.setAttribute('aria-disabled', 'true');
      const lineErrors = this.querySelector('.js-product-form-errors');

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      fetchRequestOpts.body = JSON.stringify({
        id: this.dataset.variantId,
        quantity
      });

      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        const oldQuantity = parseInt(this.quantityInput.dataset.initialValue, 10);
        this.updateQuantityState(oldQuantity);
      } finally {
        this.updateQuantityState(parseInt(quantity, 10));
        this.quantitySelector.removeAttribute('aria-disabled');
        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      }
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}

// Bundle pages load the Loop subscription implementation with `defer` from the
// product section. Load the discount compatibility patch immediately after that
// script becomes available, without changing the large product section file.
(() => {
  if (!document.querySelector('[data-loop-custom-bundle]')) return;
  if (document.querySelector('script[data-ftr-loop-discount-fix]')) return;

  const loadPatch = () => {
    if (!window.FTRLoopBundle) {
      setTimeout(loadPatch, 25);
      return;
    }

    const script = document.createElement('script');
    script.src = `${window.Shopify?.routes?.root || '/'}cdn/shop/t/0/assets/ftr-loop-bundle-discount-fix.js`;
    script.defer = true;
    script.dataset.ftrLoopDiscountFix = 'true';
    document.head.appendChild(script);
  };

  loadPatch();
})();